import { hasAccess } from "$root/components/AuthorizationGuard";
import type { BannerItem } from "$root/components/banner-stack/types";
import type { IUser } from "$root/functional-areas/user";
import type { InstrumentEditorBuilderProps } from "../builder";
import type { InstrumentEntities } from "../const";

export type MinNumberOfInstrumentBannerDeps = {
	/** Omitted: the editor has no minimum. */
	minThreshold?: number;
	instrumentBuilder: Pick<InstrumentEditorBuilderProps, "getComposition">;
	entity: InstrumentEntities;
	user: IUser;
};

export function minNumberOfInstrumentBanner({
	minThreshold,
	instrumentBuilder,
	entity,
	user,
}: MinNumberOfInstrumentBannerDeps): BannerItem | null {
	if (minThreshold == null || hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })) {
		return null;
	}

	const instrumentsInComposition = instrumentBuilder
		.getComposition({ removeDeleted: true })
		.reduce((sum, instrument) => {
			if (instrument.proxyOverwriteType === "PORTFOLIO_MIXED" && entity !== "UNIVERSE") {
				const instrumentsInInvestment = instrument.nofInstruments ?? 0;
				const instrumentsExcluded = instrument.nOfInstrumentExcluded ?? 0;
				return instrumentsInInvestment - instrumentsExcluded + sum;
			}

			if (entity === "UNIVERSE") {
				return sum + 1;
			}

			if (!instrument.weight) {
				return sum;
			}

			return sum + 1;
		}, 0);
	if (instrumentsInComposition >= minThreshold) {
		return null;
	}

	return {
		id: "min-number-of-instruments",
		severity: "info",
		title: "Minimum number of instrument",
		description: `You need to add at least ${minThreshold} instruments or a portfolio`,
	};
}
